import React from 'react';
import { Row, Col, Badge, ProgressBar } from 'react-bootstrap';
import Card from '../Card';
import { formatDisplayDate } from '../../utilities/displayFormat';

// The family's billing plan from POST billing/overview: the daily rate for
// each day type and the active subsidy (discount rules + absence days
// left). Display only - every figure comes from the API, which reads it
// from the admin's own billing set-up (Student List -> subsidy icon).

const WEEKDAY_LABELS = { 1: 'Mon', 2: 'Tue', 3: 'Wed', 4: 'Thu', 5: 'Fri', 6: 'Sat', 7: 'Sun' };
const DAY_TYPE_LABELS = { any: 'Any day', full_day: 'Full days', half_day: 'Half days' };
const ALLOWANCE_LABELS = { sick: 'Sick days', vacation: 'Vacation days', combined: 'Sick or vacation days' };

function money(value) {
  return `$${Number(value || 0).toFixed(2)}`;
}

function period(start, end) {
  return `${formatDisplayDate(start)} – ${end ? formatDisplayDate(end) : 'ongoing'}`;
}

function discountLabel(rule) {
  return rule.discount_type === 'percent' ? `${Number(rule.amount)}% off` : `${money(rule.amount)} off`;
}

function weekdaysLabel(weekdays) {
  if (!Array.isArray(weekdays) || weekdays.length === 0) return 'Every day';
  return weekdays.map((d) => WEEKDAY_LABELS[d] || d).join(', ');
}

const BillingPlan = ({ plan }) => {
  if (!plan) return null;

  if (!plan.profile_available) {
    return (
      <div className="alert alert-light border small mb-3">
        <i className="ri-information-line me-1"></i>
        Rate and subsidy details are temporarily unavailable. Your balance and invoices below are up to date.
      </div>
    );
  }

  const rates = Array.isArray(plan.rates) ? plan.rates : [];
  const subsidy = plan.subsidy;

  return (
    <Row>
      <Col lg={subsidy ? 5 : 12} className="mb-3">
        <Card className="h-100">
          <Card.Header>
            <div className="header-title">
              <h5 className="card-title mb-0">Daily Rates</h5>
              {plan.grade?.name && <small className="text-muted">{plan.grade.name}</small>}
            </div>
          </Card.Header>
          <Card.Body className="p-0">
            {rates.map((r) => (
              <div key={r.rate_type} className="d-flex justify-content-between align-items-center border-bottom px-3 py-2">
                <div>
                  <div className="fw-semibold">{r.label}</div>
                  {r.is_set && <div className="text-muted small">{period(r.effective_start_date, r.effective_end_date)}</div>}
                </div>
                {r.is_set
                  ? <h5 className="mb-0">{money(r.amount)}<small className="text-muted fw-normal"> /day</small></h5>
                  : <span className="text-muted small">Not offered</span>}
              </div>
            ))}
            <div className="text-muted small px-3 py-2">Charged per attended day, before any subsidy.</div>
          </Card.Body>
        </Card>
      </Col>

      {subsidy && (
        <Col lg={7} className="mb-3">
          <Card className="h-100">
            <Card.Header>
              <div className="header-title d-flex align-items-center gap-2">
                <h5 className="card-title mb-0">Subsidy</h5>
                <Badge bg="success">Active</Badge>
              </div>
            </Card.Header>
            <Card.Body>
              <h6 className="mb-1">{subsidy.program?.name}</h6>
              {subsidy.program?.description && <p className="text-muted small mb-1">{subsidy.program.description}</p>}
              {subsidy.reference_number && <div className="text-muted small mb-3">Reference #{subsidy.reference_number}</div>}

              <div className="fw-semibold small text-uppercase text-muted mb-1">Discounts</div>
              {(subsidy.rules || []).length === 0 ? (
                <p className="text-muted small">No discounts are active yet.</p>
              ) : (
                <ul className="list-unstyled mb-3">
                  {subsidy.rules.map((rule, i) => (
                    <li key={i} className="d-flex justify-content-between border-bottom py-1">
                      <span>
                        <strong className="text-success">{discountLabel(rule)}</strong>{' '}
                        <span className="text-muted small">
                          · {weekdaysLabel(rule.weekdays)} · {DAY_TYPE_LABELS[rule.day_type] || 'Any day'}
                        </span>
                      </span>
                      <span className="text-muted small">{period(rule.start_date, rule.end_date)}</span>
                    </li>
                  ))}
                </ul>
              )}

              {(subsidy.absence_allowances || []).length > 0 && (
                <>
                  <div className="fw-semibold small text-uppercase text-muted mb-1">Covered absences</div>
                  {subsidy.absence_allowances.map((a, i) => {
                    const pct = a.allowed_days > 0 ? Math.min(100, Math.round((a.used_days / a.allowed_days) * 100)) : 100;
                    return (
                      <div key={i} className="mb-2">
                        <div className="d-flex justify-content-between small">
                          <span>{ALLOWANCE_LABELS[a.type] || a.type}</span>
                          <span><strong>{a.remaining_days}</strong> of {a.allowed_days} left</span>
                        </div>
                        <ProgressBar now={pct} variant={a.remaining_days === 0 ? 'danger' : 'info'} style={{ height: 6 }} />
                      </div>
                    );
                  })}
                  <div className="text-muted small">Absences within these days are still covered by the subsidy.</div>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      )}
    </Row>
  );
};

export default BillingPlan;
